import { createSelector } from "@reduxjs/toolkit";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserRewardsThunk as onGetUserRewardsThunk } from "../../../slices/users/thunk";
import { getFileUrl } from "../../../utils/GetAuthUser";
import { Header } from "../../../components/header/Header";
import { Link } from "react-router-dom";


const Rewards = () => {
  const dispatch = useDispatch();
  const fileUrl = getFileUrl();

  const [selectedRewardType, setSelectedRewardType] = useState("");
  const [qrModal, setQrModal] = useState({ open: false, src: "", visible: false });

  const selectUserState = (state) => state.user;
  const userSelector = createSelector(selectUserState, (user) => ({
    isLoading: user.isLoading,
    userRewards: user.userRewards || [],
  }));

  const { isLoading, userRewards } = useSelector(userSelector);

  useEffect(() => {
    dispatch(onGetUserRewardsThunk({ rewardType: null }));
  }, [dispatch]);

  const handleRewardTypeChange = (e) => {
    const value = e.target.value;
    setSelectedRewardType(value);
    let rewardType = null;
    if (value === "amount") rewardType = "amount";
    else if (value === "gift_card") rewardType = "gift_card";
    dispatch(onGetUserRewardsThunk({ rewardType }));
  };

  const openQrModal = (src) => {
    setQrModal({ open: true, src, visible: false });
    // Small delay to trigger CSS transition
    setTimeout(() => setQrModal((prev) => ({ ...prev, visible: true })), 10);
  };

  const closeQrModal = () => {
    setQrModal((prev) => ({ ...prev, visible: false }));
    setTimeout(() => setQrModal({ open: false, src: "", visible: false }), 300);
  };

  return (
    <>
      <header className="fixed top-0 left-0 md:left-[205px] md:w-[calc(100%-205px)] z-40 bg-white w-full h-14 md:h-[84px] flex items-center justify-center px-4 sm:px-[51px] border-b border-border_color">
        <Header title="Rewards" showBack={true} />
      </header>

      <div className="w-full xl:max-[70%] flex flex-col border border-border_color rounded-[14px] p-5 mt-4">
        <div className="w-full flex items-center justify-between mt-4">
          <span className="font-bold text-black text-[22px] font-grotesk">
            Rewards
          </span>
          <Link
            to="/admin/create-reward"
            className="bg-primary text-white px-4 py-2 rounded-lg shadow hover:bg-primary/90 transition"
          >
            Add Reward
          </Link>
        </div>

        <div
          className="overflow-x-auto overflow-y-auto"
          style={{ maxHeight: "calc(85vh - 120px)" }}
        >
          <table className="w-full min-w-[700px]">
            <thead>
              <tr className="border-b border-border_color">
                <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">Sr No.</th>
                <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">Reward Type</th>
                <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">Amount</th>
                <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">Gift Card Number</th>
                <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">QR Code</th>
                <th className="text-[16px] tracking-wider leading-10 text-black font-bold font-grotesk px-3 py-3 text-left">Action</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, index) => (
                  <tr
                    key={`skeleton-${index}`}
                    className={(index + 1) % 2 === 0 ? "bg-[#2878870F]" : "bg-transparent"}
                  >
                    <td className="px-3 py-3 first:rounded-l-lg last:rounded-r-lg bg-transparent">
                      <div className={`h-7 w-7 rounded-lg ${(index + 1) % 2 === 0 ? "bg-white" : "bg-[#2878870F]"} animate-pulse`} />
                    </td>
                    <td className="px-3 py-3"><div className="h-4 w-20 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="px-3 py-3"><div className="h-4 w-16 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="px-3 py-3"><div className="h-4 w-32 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="px-3 py-3"><div className="w-16 h-16 bg-gray-200 rounded animate-pulse" /></td>
                    <td className="px-3 py-3"><div className="w-16 h-16 bg-gray-200 rounded animate-pulse" /></td>
                  </tr>
                ))
              ) : userRewards && userRewards.length > 0 ? (
                userRewards.map((reward, index) => (
                  <tr
                    key={reward.id || index}
                    className={(index + 1) % 2 === 0 ? "bg-[#2878870F]" : "bg-transparent"}
                  >
                    <td className="px-3 py-3 first:rounded-l-lg last:rounded-r-lg bg-transparent">
                      <div className={`h-7 w-7 rounded-lg flex items-center justify-center text-sm tracking-wider font-medium font-grotesk text-black ${(index + 1) % 2 === 0 ? "bg-white" : "bg-[#2878870F]"}`}>
                        {index + 1}
                      </div>
                    </td>

                    <td className="px-3 py-3">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold font-grotesk ${reward.reward_type === "amount" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                        {reward.reward_type === "amount" ? "Amount" : "Gift Card"}
                      </span>
                    </td>

                    <td className="px-3 py-3 text-sm tracking-wider font-medium font-grotesk text-black">
                      {reward.reward_type === "amount" && reward.amount ? `$${reward.amount}` : "-"}
                    </td>

                    <td className="px-3 py-3 text-sm tracking-wider font-medium font-grotesk text-black">
                      {reward.reward_type === "gift_card" && reward.gift_card_number ? reward.gift_card_number : "-"}
                    </td>

                    {/* QR Code — clickable */}
                    <td className="px-3 py-3 first:rounded-l-lg last:rounded-r-lg bg-transparent">
                      {reward.reward_type === "gift_card" && reward.gift_card_qr_code ? (
                        <div
                          className="w-16 h-16 rounded overflow-hidden border border-border_color cursor-pointer hover:scale-110 hover:shadow-lg transition-transform duration-200"
                          onClick={() => openQrModal(`${fileUrl}/${reward.gift_card_qr_code}`)}
                          title="Click to enlarge"
                        >
                          <img
                            src={`${fileUrl}/${reward.gift_card_qr_code}`}
                            alt="QR Code"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.parentElement.innerHTML = '<span class="text-xs text-gray-400">No QR</span>';
                            }}
                          />
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>

                    <td>
                      <Link
                        to={`/admin/edit-reward/${reward.id}`}
                        className="text-blue-600 hover:underline text-sm"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="py-6 text-center text-xl tracking-wider font-bold font-grotesk text-gray-500">
                    No Rewards Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* QR Code Modal */}
      {qrModal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{
            backgroundColor: qrModal.visible ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0)",
            backdropFilter: qrModal.visible ? "blur(4px)" : "blur(0px)",
            transition: "background-color 0.3s ease, backdrop-filter 0.3s ease",
          }}
          onClick={closeQrModal}
        >
          <div
            className="relative bg-white rounded-2xl p-6 shadow-2xl flex flex-col items-center gap-4"
            style={{
              transform: qrModal.visible ? "scale(1) translateY(0)" : "scale(0.7) translateY(40px)",
              opacity: qrModal.visible ? 1 : 0,
              transition: "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeQrModal}
              className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-900 transition-colors text-lg font-bold"
              aria-label="Close"
            >
              ✕
            </button>

            <p className="text-base font-bold font-grotesk text-gray-700 tracking-wide">QR Code</p>

            <div className="w-64 h-64 rounded-xl overflow-hidden border border-border_color shadow-md">
              <img
                src={qrModal.src}
                alt="QR Code Full"
                className="w-full h-full object-contain"
              />
            </div>

            <p className="text-xs text-gray-400">Click outside or ✕ to close</p>
          </div>
        </div>
      )}
    </>
  );
};

export default Rewards;